import { useState, useCallback } from 'react';
import { Tooltip } from '../display/Tooltip';
import { getInitials, normalizeAvatarUrl } from '@airclou/utils';

interface AvatarUser {
  id: string;
  name: string;
  avatar?: string;
  email?: string;
}

interface AvatarStackProps {
  users: AvatarUser[];
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  showTooltip?: boolean;
  variant?: 'default' | 'blue';
  borderColor?: string;
}

export function AvatarStack({ 
  users, 
  maxVisible = 4, 
  size = 'md',
  className = '',
  showTooltip = true,
  variant = 'default',
  borderColor
}: AvatarStackProps) {
  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = Math.max(0, users.length - maxVisible);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  /**
   * Photo addresses that failed to load — keyed by the address, not by the user
   * it belongs to. Keyed by user id, a member who replaces a dead profile photo
   * stays on initials for the life of the mount: the set still names them, so
   * the working address never gets rendered. `Avatar` remembers the same thing
   * the same way.
   */
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set());

  const handleImageError = useCallback((avatarUrl: string) => {
    setFailedAvatars(prev => {
      if (prev.has(avatarUrl)) return prev;
      const next = new Set(prev);
      next.add(avatarUrl);
      return next;
    });
  }, []);

  const sizeClasses = {
    xs: 'w-5 h-5 text-[9px]',
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm'
  };

  const getTooltipContent = (user: AvatarUser) => {
    const parts = [user.name];
    if (user.email) {
      parts.push(user.email);
    }
    // Check if user has status (for collaborators)
    if ('status' in user) {
      const status = (user as any).status;
      if (status) {
        parts.push(`Status: ${status}`);
      }
    }
    return parts.join(' • ');
  };

  const getBorderClasses = () => {
    if (borderColor) {
      return `border-2 border-[${borderColor}]`;
    }
    return 'border-2 border-white dark:border-gray-800';
  };

  const getAvatarClasses = () => {
    const baseClasses = `relative ${sizeClasses[size]} rounded-full ${getBorderClasses()} flex items-center justify-center font-medium shadow-sm cursor-default`;
    
    if (variant === 'blue') {
      return `${baseClasses} bg-primary text-primary-foreground`;
    }
    
    return `${baseClasses} bg-gray-300 dark:bg-gray-400 text-gray-700 dark:text-gray-900`;
  };

  // Format large numbers for better display in small circles
  const formatOverflowCount = (count: number) => {
    if (count >= 1000) {
      return `+${Math.floor(count / 1000)}k`;
    }
    if (count >= 100) {
      return `+${count}`;
    }
    return `+${count}`;
  };

  // Get appropriate font size for overflow count
  const getOverflowFontSize = (count: number) => {
    const baseSizeClasses = {
      xs: count >= 100 ? 'text-[7px]' : 'text-[9px]',
      sm: count >= 100 ? 'text-[8px]' : 'text-[10px]',
      md: count >= 100 ? 'text-[10px]' : 'text-xs',
      lg: count >= 100 ? 'text-xs' : 'text-sm'
    };
    return baseSizeClasses[size];
  };

  const getOverflowClassesWithCount = (count: number) => {
    const baseClasses = `relative ${sizeClasses[size]} rounded-full ${getBorderClasses()} flex items-center justify-center font-medium shadow-sm cursor-default ${getOverflowFontSize(count)}`;
    return `${baseClasses} bg-gray-400 dark:bg-gray-600 text-white`;
  };

  return (
    <div className={`flex items-center -space-x-2 ${className}`}>
      {visibleUsers.map((user, index) => {
        // Resolved once: it is both what the <img> loads and the key the
        // failure set remembers, so the two can't name different addresses.
        const avatarUrl = normalizeAvatarUrl(user.avatar);
        return (
          <Tooltip
            key={user.id}
            content={showTooltip ? getTooltipContent(user) : ''}
            show={showTooltip && hoveredIndex === index}
            position="top"
            delay={300}
            disabled={!showTooltip}
          >
            <div
              className={getAvatarClasses()}
              style={{ zIndex: maxVisible - index }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {avatarUrl && !failedAvatars.has(avatarUrl) ? (
                <img
                  src={avatarUrl}
                  alt={user.name}
                  className="w-full h-full rounded-full object-cover"
                  onError={() => handleImageError(avatarUrl)}
                />
              ) : (
                <span>{getInitials(user.name)}</span>
              )}
            </div>
          </Tooltip>
        );
      })}
      {remainingCount > 0 && (
        <Tooltip
          content={showTooltip ? `${remainingCount} more member${remainingCount > 1 ? 's' : ''}` : ''}
          show={showTooltip && hoveredIndex === visibleUsers.length}
          position="top"
          delay={300}
          disabled={!showTooltip}
        >
          <div
            className={getOverflowClassesWithCount(remainingCount)}
            style={{ zIndex: 0 }}
            onMouseEnter={() => setHoveredIndex(visibleUsers.length)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {formatOverflowCount(remainingCount)}
          </div>
        </Tooltip>
      )}
    </div>
  );
}